// Embed 交互 Bridge —— widget ↔ embed iframe 内容 的受控白名单 postMessage 协议。
// spec: docs/superpowers/specs/2026-08-07-native-chat-ui-design.md
//       「补充(2026-08-08):Embed 交互 Bridge(受控白名单)」
//
// 分层(勿与 SDK bridge 混淆):
//   SDK bridge(protocol v1)  宿主 App(native) ↔ widget    —— 已有(native-bridge.js)
//   Embed bridge(本文件)     widget           ↔ embed iframe 内容
//
// 死 iframe 无意义 —— embed 组件(小游戏/投票/表单卡)要能双向通信。但 embed 内容
// 可能是第三方(embed_url),已被 sandbox(无 allow-same-origin)隔离碰不到宿主
// token/bridge。所以交互**不是任意 RPC,是固定枚举的受控白名单**。
//
// 安全红线(第一行就做对):
//   1. 双向校验 event.origin + event.source;能力是 switch 白名单,未知类型丢弃。
//      - sandbox 无 allow-same-origin ⇒ 嵌入文档是 opaque origin ⇒ 它 postMessage
//        上来的 event.origin 恒为字符串 'null'(不是它的 embed_url origin!)。
//        因此**真正不可伪造的绑定是 event.source === iframe.contentWindow**
//        (contentWindow 是只有该 iframe 才持有的 window 引用,攻击者无法伪造)。
//        origin 校验作纵深防御:必须是我们 sandbox 产出的 opaque 哨兵 'null'
//        (或——万一将来放开 same-origin——等于 embed_url 的 origin)。
//   2. embed 上报的 event/submit 一律当**不可信访客输入**:调用方复用现有访客
//      消息/postback 的校验限流路径,metadata 标 source=embed_event。不因"是我们
//      的 embed"就信任(embed_url 可能第三方)。
//   3. init 载荷是**非敏感白名单**:theme/lang/mainColor/state —— 绝不含
//      token/session/visitor PII。buildInitPayload 用 INIT_ALLOWED_KEYS 死锁。
//
// 本文件是纯逻辑(零 React / 零 DOM 依赖,只碰传入的 window 句柄),便于 node --test
// 直接 import,也便于 native 侧(Android/iOS WebView JS 接口)对照移植同一套语义。

/** 协议版本(信封 v 字段)。 */
export const EMBED_BRIDGE_VERSION = 1;

/** 信封命名空间(过滤掉页面上其它 postMessage 流量)。 */
export const EMBED_MSG_NS = 'lane-embed';

/** 高度夹上限,与 embed-message.js / 服务端 RichMessageValidator 一致。 */
export const EMBED_BRIDGE_MAX_HEIGHT = 480;

/** sandbox(无 allow-same-origin)opaque origin 上报出的哨兵值。 */
export const EMBED_OPAQUE_ORIGIN = 'null';

/** embed → widget 能力(固定枚举,非任意方法)。 */
export const INBOUND = Object.freeze({
    READY: 'ready',
    EVENT: 'event',
    NOTIFY: 'notify',
    RESIZE: 'resize',
    CLOSE: 'close',
    OPEN_URL: 'openUrl',
    SUBMIT: 'submit',
});

/** widget → embed 通知。 */
export const OUTBOUND = Object.freeze({
    INIT: 'init',
    THEME: 'theme',
});

/**
 * init 唯一允许跨到(可能第三方的)embed 的键。改这个列表 = 改安全边界,
 * 单测锁死("init 不泄敏感字段")。**永不加** token/session/visitor_uuid/email/PII。
 *   - theme      light|dark,embed 跟随暗色
 *   - lang       BCP-47 语言,embed 本地化
 *   - mainColor  品牌主色(展示用,非敏感)
 *   - state      服务端权威的完成态(如投票结果),用于历史富化"已完成"只读渲染;
 *                由服务端产出并过白名单,是展示内容不是凭证
 */
export const INIT_ALLOWED_KEYS = Object.freeze(['theme', 'lang', 'mainColor', 'state']);

/** openUrl 允许的 scheme(与 message-area 链接纪律一致:https?|mailto|tel)。 */
const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:)/i;

/** notify/event 的 name 长度上限(防垃圾);data 序列化后字节上限。 */
const MAX_NAME_LEN = 64;
const MAX_DATA_BYTES = 16 * 1024;

/**
 * 高度夹取:正整数,上限 480。非法 → null(调用方忽略该 resize)。
 */
export function clampBridgeHeight(raw) {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(n, EMBED_BRIDGE_MAX_HEIGHT);
}

/**
 * 某个 embed entry 可接受的 origin 集合。
 * sandbox opaque ⇒ 恒含哨兵 'null';url 模式再宽容地带上其 embed_url 的 origin
 * (纵深,真正把关的是 source 绑定)。
 * @param {{src?:string}} entry
 * @returns {Set<string>}
 */
export function acceptedOriginsForEmbed(entry) {
    const set = new Set([EMBED_OPAQUE_ORIGIN]);
    const src = entry && typeof entry.src === 'string' ? entry.src : '';
    if (src) {
        try { set.add(new URL(src).origin); } catch (e) { /* 非法 src 忽略 */ }
    }
    return set;
}

/**
 * 双向信任判定:必须是我们注册过的那个 iframe 的 contentWindow(不可伪造)
 * **且** origin 落在该 entry 的可接受集合内。
 * @param {MessageEvent} event
 * @param {{win:Window, src?:string}} entry
 */
export function isTrustedEmbedMessage(event, entry) {
    if (!event || !entry || !entry.win) return false;
    if (!event.source || event.source !== entry.win) return false;   // 强绑定
    return acceptedOriginsForEmbed(entry).has(event.origin);          // 纵深
}

/** 信封识别:是不是我们这套协议的入站帧。 */
export function isEmbedInboundEnvelope(data) {
    return !!data && typeof data === 'object'
        && data.ns === EMBED_MSG_NS
        && typeof data.type === 'string';
}

/**
 * 构造 init 载荷 —— 只从 context 里挑白名单键,其余(包括任何敏感字段)一律丢弃。
 * 即使 context 里混进了 token/session/email 也进不了产物。
 * @param {object} context 任意上下文(可能含敏感字段)
 * @returns {{ns,v,type,theme?,lang?,mainColor?,state?}}
 */
export function buildInitPayload(context) {
    const out = { ns: EMBED_MSG_NS, v: EMBED_BRIDGE_VERSION, type: OUTBOUND.INIT };
    const ctx = context && typeof context === 'object' ? context : {};
    INIT_ALLOWED_KEYS.forEach((k) => {
        if (ctx[k] !== undefined && ctx[k] !== null) out[k] = ctx[k];
    });
    return out;
}

/** 构造 theme 通知载荷。 */
export function buildThemePayload(theme) {
    return { ns: EMBED_MSG_NS, v: EMBED_BRIDGE_VERSION, type: OUTBOUND.THEME, theme: theme === 'dark' ? 'dark' : 'light' };
}

/** notify/event 的 name 归一:字符串、trim、截断。非字符串 → ''。 */
function sanitizeEventName(name) {
    if (typeof name !== 'string') return '';
    return name.trim().slice(0, MAX_NAME_LEN);
}

/**
 * notify/event/submit 的 data 归一:必须是可 JSON 序列化的普通对象,且序列化后
 * 不超上限。任何异常 / 超限 → null。这只是"别发垃圾"的客户端礼貌,**权威校验在
 * 服务端**(embed 上报是不可信输入)。
 */
function sanitizeEventData(data) {
    if (data === undefined || data === null) return null;
    if (typeof data !== 'object') return null;
    let json;
    try { json = JSON.stringify(data); } catch (e) { return null; }
    if (typeof json !== 'string') return null;
    if (json.length > MAX_DATA_BYTES) return null;
    try { return JSON.parse(json); } catch (e) { return null; }
}

/** openUrl 的 url 归一:scheme 白名单外一律 null(不放行)。 */
export function safeBridgeUrl(url) {
    if (typeof url !== 'string') return null;
    const u = url.trim();
    return SAFE_URL_RE.test(u) ? u : null;
}

/**
 * 分发器工厂。持一个 source(contentWindow)→ entry 的注册表,window 'message'
 * 事件进来先双向校验再按能力 switch 分发。widget 侧动作由 deps 注入(便于测试
 * 打桩,也便于 chat.js 把真实副作用——发消息 / openLink——接进来)。
 *
 * @param {{
 *   onNotify?: (entry, name, data) => void,   // event/notify/submit → 转 postback(不可信)
 *   onOpenUrl?: (url, entry) => void,         // openUrl → 宿主 linkPolicy
 * }} deps
 */
export function createEmbedBridge(deps = {}) {
    /** @type {Map<any, {id:any, win:Window, src?:string, embedMessageId?:any,
     *                    initContext?:object, onResize?:Function, onClose?:Function,
     *                    onEvent?:Function}>} */
    const registry = new Map();

    function findBySource(source) {
        if (!source) return null;
        for (const entry of registry.values()) {
            if (entry.win === source) return entry;
        }
        return null;
    }

    function postToEmbed(entry, payload) {
        if (!entry || !entry.win || typeof entry.win.postMessage !== 'function') return;
        // targetOrigin '*':sandbox opaque 的 iframe 无法用具名 origin 定址;这安全,
        // 因为载荷是 buildInitPayload/buildThemePayload 的非敏感白名单产物(无 PII)。
        try { entry.win.postMessage(payload, '*'); } catch (e) { /* best-effort */ }
    }

    const bridge = {
        /** 注册一个 embed iframe。id 唯一(用 embedMessageId 或自增)。 */
        register(entry) {
            if (!entry || !entry.win) return;
            registry.set(entry.id, entry);
        },
        unregister(id) { registry.delete(id); },
        has(id) { return registry.has(id); },
        size() { return registry.size; },

        /** 主动给某个 embed 发 init(通常在收到 ready 时,或注册后想立即握手)。 */
        sendInit(id) {
            const entry = registry.get(id);
            if (!entry) return;
            postToEmbed(entry, buildInitPayload(entry.initContext));
        },

        /** 主题切换 → 广播给所有在册 embed。 */
        broadcastTheme(theme) {
            const payload = buildThemePayload(theme);
            for (const entry of registry.values()) postToEmbed(entry, payload);
        },

        /** window 'message' 处理:双向校验 → 能力 switch 白名单 → 未知丢弃。 */
        handleMessage(event) {
            const data = event && event.data;
            if (!isEmbedInboundEnvelope(data)) return;           // 非本协议 → 忽略
            const entry = findBySource(event.source);
            if (!entry) return;                                  // 未注册来源 → 丢弃
            if (!isTrustedEmbedMessage(event, entry)) return;    // origin/source 校验失败 → 丢弃

            switch (data.type) {
                case INBOUND.READY:
                    postToEmbed(entry, buildInitPayload(entry.initContext));
                    return;

                case INBOUND.EVENT:
                case INBOUND.NOTIFY: {
                    const name = sanitizeEventName(data.name);
                    if (!name) return;                           // 无名事件 → 丢
                    const payload = sanitizeEventData(data.data);
                    if (typeof deps.onNotify === 'function') {
                        deps.onNotify(entry, name, payload);
                    }
                    // 容器层本地回执(纯 UI,不影响上面的不可信输入纪律)。
                    if (typeof entry.onEvent === 'function') {
                        try { entry.onEvent(name, payload); } catch (e) { /* UI 反馈失败不牵连主链路 */ }
                    }
                    return;
                }

                case INBOUND.SUBMIT: {
                    // submit 同 event 的不可信纪律:统一转 postback,name 固定 'submit'。
                    const payload = sanitizeEventData(data.data);
                    if (typeof deps.onNotify === 'function') {
                        deps.onNotify(entry, 'submit', payload);
                    }
                    if (typeof entry.onEvent === 'function') {
                        try { entry.onEvent('submit', payload); } catch (e) { /* 同上 */ }
                    }
                    return;
                }

                case INBOUND.RESIZE: {
                    const h = clampBridgeHeight(data.height);
                    if (h != null && typeof entry.onResize === 'function') entry.onResize(h);
                    return;
                }

                case INBOUND.CLOSE:
                    if (typeof entry.onClose === 'function') entry.onClose();
                    return;

                case INBOUND.OPEN_URL: {
                    const url = safeBridgeUrl(data.url);
                    if (url && typeof deps.onOpenUrl === 'function') deps.onOpenUrl(url, entry);
                    return;
                }

                default:
                    return;                                      // 未知能力 → 静默丢弃
            }
        },
    };

    return bridge;
}
